/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Send, Loader2, AlertTriangle, Info, Megaphone, Paperclip, X, Search, ChevronLeft } from 'lucide-react';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';

const TYPE_CONFIG = {
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  info: { label: 'Info', color: 'bg-blue-100 text-blue-700', icon: Info },
  general: { label: 'General', color: 'bg-gray-100 text-gray-600', icon: Megaphone },
};

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

function fmt(d) {
  if (!d) return '—';
  const date = new Date(d);
  const diff = Date.now() - date;
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export default function UserAnnouncements() {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [search, setSearch] = useState('');
  const repliesEndRef = useRef(null);

  useEffect(() => { fetchAnnouncements(); }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    const res = await adminServices.getAnnouncements();
    if (res.ok) setAnnouncements(res.data || []);
    setLoading(false);
  }

  async function openAnnouncement(ann) {
    setSelected(ann);
    setReplies([]);
    setReplyText('');
    adminServices.markAnnouncementRead(ann.id);
    setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, isRead: true } : a));
    if (ann.allowReplies) {
      setLoadingReplies(true);
      const res = await adminServices.getAnnouncementReplies(ann.id);
      if (res.ok) setReplies(res.data || []);
      setLoadingReplies(false);
    }
  }

  async function handleAcknowledge() {
    if (!selected) return;
    await adminServices.acknowledgeAnnouncement(selected.id);
    setSelected(prev => ({ ...prev, isAcknowledged: true }));
    setAnnouncements(prev => prev.map(a => a.id === selected.id ? { ...a, isAcknowledged: true } : a));
  }

  async function handleSendReply() {
    if (!replyText.trim() || !selected) return;
    const message = replyText.trim();
    setReplyText('');
    const optimistic = { id: Date.now(), message, sentAt: new Date().toISOString(), role: 'AUTH_USER' };
    setReplies(prev => [...prev, optimistic]);
    setTimeout(() => repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    const res = await adminServices.createAnnouncementReply(selected.id, { message });
    if (res.ok) setReplies(prev => prev.map(r => r.id === optimistic.id ? { ...res.data, role: 'AUTH_USER' } : r));
  }

  const filtered = announcements.filter(a =>
    (a.title || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Page title="Announcements" backButton>
      <div className="bg-gray-50 flex flex-col" style={{ height: 'calc(100vh - 65px)' }}>
        <div className="flex flex-1 overflow-hidden">

          {/* ── Left Panel: Announcement List ── */}
          <div className={`flex flex-col border-r border-gray-200 bg-white ${selected ? 'hidden md:flex md:w-[340px] lg:w-[380px]' : 'w-full md:w-[340px] lg:w-[380px]'} flex-shrink-0`}>
            {/* Header */}
            <div className="px-4 py-4 border-b border-gray-100">
              <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-3">
                <button
                  onClick={() => navigate('/app/dashboard')}
                  className="p-1 -ml-1 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Back to dashboard"
                >
                  <ChevronLeft size={20} className="text-gray-500" />
                </button>
                <Bell size={18} className="text-purple-600" /> Announcements
              </h1>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 bg-gray-50" />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="flex gap-3 animate-pulse">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
                      <div className="flex-1"><div className="h-4 w-32 bg-gray-200 rounded mb-2" /><div className="h-3 w-48 bg-gray-100 rounded" /></div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <Bell size={36} className="text-gray-200 mb-3" />
                  <p className="text-sm text-gray-400">{search ? 'No results' : 'No announcements yet'}</p>
                </div>
              ) : (
                filtered.map(ann => {
                  const TypeIcon = TYPE_CONFIG[ann.type]?.icon || Megaphone;
                  const isActive = selected?.id === ann.id;
                  return (
                    <div key={ann.id} onClick={() => openAnnouncement(ann)}
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-colors ${isActive ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${ann.type === 'urgent' ? 'bg-red-100' : ann.type === 'info' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <TypeIcon size={16} className={TYPE_CONFIG[ann.type]?.color?.split(' ')[1] || 'text-gray-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm truncate ${!ann.isRead ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{ann.title}</p>
                          <span className="text-xs text-gray-400 flex-shrink-0">{fmt(ann.sentAt)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{stripHtml(ann.body)}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          {ann.requiresAck && !ann.isAcknowledged && <span className="w-2 h-2 rounded-full bg-amber-400" />}
                          {ann.isAcknowledged && <CheckCheck size={12} className="text-green-500" />}
                        </div>
                      </div>
                      {!ann.isRead && <div className="w-2.5 h-2.5 rounded-full bg-purple-500 flex-shrink-0 mt-2" />}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Right Panel: Detail / Chat ── */}
          <div className={`flex-1 flex flex-col bg-gray-50 ${!selected ? 'hidden md:flex' : 'flex'}`}>
            {!selected ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                <div className="w-20 h-20 rounded-full bg-purple-50 flex items-center justify-center mb-4">
                  <Bell size={32} className="text-purple-300" />
                </div>
                <p className="text-gray-500 font-medium">Select an announcement</p>
                <p className="text-sm text-gray-400 mt-1">Choose from the list to view details</p>
              </div>
            ) : (
              <>
                {/* Detail Header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
                  <button onClick={() => setSelected(null)} className="p-1 rounded-lg hover:bg-gray-100">
                    <X size={18} className="text-gray-500" />
                  </button>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selected.type === 'urgent' ? 'bg-red-100' : selected.type === 'info' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    {(() => { const I = TYPE_CONFIG[selected.type]?.icon || Megaphone; return <I size={16} className={TYPE_CONFIG[selected.type]?.color?.split(' ')[1]} />; })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{selected.title}</p>
                    <p className="text-xs text-gray-400">{fmt(selected.sentAt)} · {TYPE_CONFIG[selected.type]?.label}</p>
                  </div>
                  {selected.requiresAck && !selected.isAcknowledged && (
                    <button onClick={handleAcknowledge}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
                      <CheckCheck size={13} /> Acknowledge
                    </button>
                  )}
                  {selected.isAcknowledged && (
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCheck size={13} /> Acknowledged</span>
                  )}
                </div>

                {/* Message body (like a chat bubble from admin) */}
                <div className="flex-1 overflow-y-auto px-4 py-4" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 5 L35 15 L30 25 L25 15Z\' fill=\'%23f3f0ff\' opacity=\'0.4\'/%3E%3C/svg%3E")' }}>
                  {/* Announcement message */}
                  <div className="max-w-[85%] mb-4">
                    <div className="bg-white rounded-xl rounded-tl-sm shadow-sm p-4 border border-gray-100">
                      <div className="prose prose-sm max-w-none text-gray-700 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: selected.body }} />
                      {selected.attachmentUrl && selected.attachmentName && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(selected.attachmentName) && (
                        <img src={selected.attachmentUrl} alt={selected.attachmentName} className="mt-3 max-w-[250px] max-h-[200px] object-cover rounded-lg border border-gray-200" />
                      )}
                      {selected.attachmentName && !(selected.attachmentUrl && /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(selected.attachmentName)) && (
                        <div className="mt-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
                          <Paperclip size={13} className="text-gray-400" />
                          <a href={selected.attachmentUrl} target="_blank" rel="noopener noreferrer" className="truncate text-purple-600 hover:underline text-xs">{selected.attachmentName}</a>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1 ml-1">{fmt(selected.sentAt)}</p>
                  </div>

                  {/* Replies */}
                  {selected.allowReplies && (
                    <>
                      {loadingReplies ? (
                        <div className="text-center py-4"><Loader2 size={16} className="animate-spin text-purple-400 mx-auto" /></div>
                      ) : (
                        <div className="space-y-3">
                          {replies.map(r => {
                            const isMe = r.role === 'AUTH_USER' || r.role === 'MANAGER';
                            return (
                              <div key={r.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[75%]`}>
                                  <div className={`rounded-xl px-3 py-2 ${isMe ? 'bg-purple-100 text-purple-900 rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'}`}>
                                    <p className="text-sm">{r.message}</p>
                                  </div>
                                  <p className={`text-xs text-gray-400 mt-0.5 ${isMe ? 'text-right mr-1' : 'ml-1'}`}>{fmt(r.sentAt)}</p>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={repliesEndRef} />
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Reply input */}
                {selected.allowReplies && (
                  <div className="px-4 py-3 bg-white border-t border-gray-200 flex gap-2 flex-shrink-0">
                    <input type="text" value={replyText} onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendReply()}
                      placeholder="Type a reply..."
                      className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-gray-50" />
                    <button onClick={handleSendReply} disabled={!replyText.trim()}
                      className="w-9 h-9 flex items-center justify-center bg-purple-600 text-white rounded-full hover:bg-purple-700 disabled:opacity-40">
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
